export default {
  unsaved: {
    title: 'Save your changes?',
    description: "You've made changes to this step that haven't been saved yet.",
    discard: 'Discard',
    save_and_continue: 'Save and continue',
  },
  title: 'Set up your restaurant',
  nav: {
    completed: 'Completed',
    locked: 'Handled by your provider',
    aria_label: 'Launch steps',
    step_label: 'Step {{number}}',
    section_label: 'Launch',
    back_to_home: 'Back to Home',
  },
  owner_invite: {
    hint: 'Prefer the restaurant owner to finish setup themselves? Invite them and they will pick up where you left off.',
    sent: 'Invite sent to {{name}} ({{email}}) on {{date}}.',
  },
  footer: {
    step_of: 'Step {{current}} of {{total}}',
  },
  buttons: {
    next_task: 'Next task',
    cancel: 'Cancel',
    back: 'Back',
    next: 'Continue',
    save: 'Save',
    exit: 'Exit setup',
    retry: 'Try again',
  },
  errors: {
    progress_load_failed: 'We could not load the setup progress for this restaurant.',
    linked_company_unavailable: 'This link is for a restaurant you can’t open, so you are on your current one.',
  },
  placeholder: {
    title: 'This step is coming soon',
    description: 'The wizard shell and progress tracking are ready. This phase will be built next.',
  },
  new_restaurant: {
    title: 'New restaurant',
    menu: {
      title: 'Menu',
      description: 'A demo skips onboarding, so import its menu now.',
    },
    demo: {
      title: 'New demo',
      description: 'A demo restaurant for testing — it skips most of onboarding.',
      label: 'Create demo',
    },
    description: 'Tell us where this restaurant is. We’ll look it up on Google Maps.',
  },
  location: {
    edit_description: 'Update where this restaurant is.',
    cta: 'Create restaurant',
    section: {
      address: 'Basic information',
      group: 'Brand',
      group_description: 'Is this location part of a brand you already have?',
    },
    form: {
      name: {
        label: 'Restaurant name',
        placeholder: 'Search for your restaurant…',
        help: 'Start typing to search Google Maps for this restaurant.',
      },
      address: {
        label: 'Street address',
        placeholder: 'Street and number',
      },
      city: {
        label: 'City',
      },
      zip_code: {
        label: 'Postal code',
      },
      country: {
        label: 'Country',
      },
      region: {
        label: 'State / Region',
        placeholder: 'Select a state or region',
      },
    },
    group: {
      aria_label: 'Brand options',
      deferred_hint: "You'll pick the brand name and web address at the last step.",
      existing: {
        title: 'Add to an existing brand',
        description: 'Another location under a brand you already run.',
        select_label: 'Brand',
        select_placeholder: 'Select a brand',
      },
      new: {
        title: 'Create a new brand',
        description: 'A single restaurant, or the first location of a new brand.',
      },
    },
    toasts: {
      update_failed: 'Could not save the location. Please try again.',
      created: 'Restaurant created',
      create_failed: 'Could not create the restaurant. Please try again.',
    },
  },
  domain: {
    url_setup: {
      title: 'Online ordering address',
      description: 'Where guests will place orders.',
    },
    modes: {
      foodamigos: {
        host_fallback: 'your online ordering domain',
        title: 'Use our generic domain',
      },
      custom: {
        title: 'Connect your own domain',
        description: 'Use a domain the restaurant already owns.',
      },
    },
    toasts: {
      save_failed: 'Could not save the online ordering address. Please try again.',
    },
  },
  go_live: {
    subtitle: 'Check that every step is done, then switch on live orders.',
    slug: {
      // Answers the open question in round-2 §8: PartialUpdateFranchiseRequest
      // carries no go-live guard on the slug, so it can be changed at any time —
      // but there is no redirect table, so the previous address stops resolving.
      // Saying so removes the hesitation without overpromising.
      help: 'Letters, numbers and hyphens only. You can change this later, but the old address will stop working.',
      title: 'Online ordering address',
      description: 'This is where your customers will order. You can change it later.',
      label: 'Customise it with the restaurant name',
      placeholder: 'bella-italia',
      preview: 'Your ordering page:',
      save: 'Save URL',
      saved: 'Online ordering address saved',
      save_failed: 'Could not save the URL. Please try again.',
      status: {
        checking: 'Checking availability…',
        available: 'Available',
        taken: 'This URL is already taken',
        invalid: 'Use only letters, numbers, hyphens and underscores',
        error: 'Could not check availability — please try again',
      },
    },
    domain: {
      title: 'Custom domain (optional)',
      description: 'Use your own domain instead of the FoodAmigos-provided address.',
      add: '+ Add a custom domain',
      hide: 'Hide custom domain',
      label: 'Your domain',
      placeholder: 'shop.myrestaurant.com',
      save: 'Save',
      show_records: 'Show DNS records',
      hint: 'For ordering only — your website needs a different domain. For example order.myrestaurant.com for the shop and myrestaurant.com for the website.',
      records_after_save:
        'Once you save the domain, we show the exact records to add at your DNS provider — a main domain (myrestaurant.com) and a subdomain (order.myrestaurant.com) need different ones.',
      saved: 'Custom domain saved',
      save_failed: 'Could not save the domain. Please try again.',
      unconfirmed: {
        title: "Going live doesn't wait for this domain",
        description:
          'Until its records are confirmed, the ordering page stays on its standard address, and the restaurant can go live there. Once they are confirmed — this can take up to 48 hours — the domain serves the ordering page automatically.',
      },
    },
    locations: {
      title: 'Confirm locations',
      description: 'Each location in your brand gets its own ordering address. Confirm or edit them below.',
      save: 'Save locations',
      saved: 'Location URLs saved',
      save_failed: 'Could not save one or more location URLs. Please try again.',
    },
    preflight: {
      title: 'Before you go live',
      description: 'Complete these to enable live orders.',
      url_setup: 'Ordering address set up',
      online_payment: {
        your_provider: 'your provider',
        payments_live: '{{title}} · done',
        online_payments_off: '{{title}} · not taking online payments',
        waiting_for_provider: '{{title}} · waiting for {{provider}}',
        hosted_not_finished: '{{title}} · finish with {{provider}}',
        action_needed: '{{title}} · action needed',
        waiting_for_partner: '{{title}} · waiting for your partner',
        not_set_up: '{{title}} · not set up',
        business_details_missing: '{{title}} · add business details for payouts',
      },
    },
    failed_checks: {
      title: 'A few things still need attention',
      items: {
        name: 'Restaurant name',
        logo_url: 'Logo',
        pictures: 'Gallery photos',
        menu_uploaded: 'Menu',
        opening_hours: 'Opening hours',
        address: 'Address',
        storefront: 'Ordering address',
        payments_configured: 'Payments',
        stripe_billing: 'Billing',
        test_orders: 'Test order',
        google_business: 'Google Business profile',
        master_promo_code: 'Welcome promo code',
        flyers_ordered: 'Flyers ordered',
        identity: 'Restaurant type & cuisines',
        billing_payment_method: 'Payment method',
        billing_license: 'Licence',
        business_name: 'Business / restaurant name',
        payment_method: 'Cash or online payments',
        stripe_processing_fee: 'Partner processing fee',
      },
      force: 'Go live anyway',
      blocker: '(required)',
      link: 'Open Partner Billing',
    },
    error: 'Could not enable live orders. Please try again.',
    gate_hint: 'Complete the checklist above to enable Go Live.',
    business_name: {
      label: 'Business / restaurant name',
      help: 'Shown on your terms of use and privacy policy. Use the name your business trades under.',
      save: 'Save',
      save_failed: 'Could not save the business name. Please try again.',
      hint: 'Save the business / restaurant name first.',
    },
    cash_only: {
      message:
        'Online payments are off — customers can only pay cash on pickup or delivery until a gateway is linked and verified.',
    },
    payment_blocked: {
      title: 'Turn on cash payments or set up online payments to go live.',
      payment_method: 'Customers need a way to pay.',
      turn_on_cash: 'Turn on cash payments',
      ask_partner: 'Ask your partner to turn on cash payments, or',
      set_up_online: 'set up online payments',
    },
    buttons: {
      go_live: 'Go Live 🚀',
      finish: 'Finish setup',
    },
    celebration: {
      title: "You're live!",
      title_named: '{{name}} is live!',
      subtitle: 'Your restaurant is now accepting live orders.',
      license: '{{plan}} · active',
      license_trial: '{{plan}} · free until {{date}}',
      finish: 'Finish setup',
    },
    service_fee: {
      title: 'Service fee',
      headline: 'Earn extra revenue without charging your restaurants',
      subtitle:
        'Add a small service fee that guests pay at checkout. You keep half. It stays off unless you turn it on.',
      subtitle_share:
        'Add a small service fee that guests pay at checkout. You keep {{share}}%. It stays off unless you turn it on.',
      tiles: {
        guest: {
          title: 'Paid by the guest',
          description: 'Shown at checkout, like on delivery apps.',
        },
        share: {
          title: 'Half goes to you',
          title_share: '{{share}}% goes to you',
          description: 'Split {{share}}/{{rest}} with FoodAmigos.',
        },
        choice: {
          title: 'Your choice',
          description: 'Off by default. Change it anytime.',
        },
      },
      preview: {
        title: 'What guests would see at checkout',
        subtotal: 'Order subtotal',
        fee: 'Service fee',
        fee_terms: '({{percentage}}%, max {{max}})',
        guest_pays: 'Guest pays',
        split_partner: '<strong>You {{amount}}</strong> per order',
        split_provider: 'FoodAmigos {{amount}}',
        unchanged: "The restaurant's order value is unchanged.",
      },
      earnings: {
        title: 'What you could earn from this restaurant',
        orders: 'Online orders a month',
        average_order: 'Average order',
        per_month: 'a month for you',
        collected: '{{amount}} collected from guests a month. Your share: {{share}}%. Estimate only.',
        effective_rate: 'Effective fee rate across all orders: {{rate}}',
      },
      choice: {
        question: 'Add a service fee for this restaurant?',
        enabled: {
          title: 'Add a service fee',
          recommended: 'Recommended: {{percentage}}%, max {{max}}',
          description: 'Guests pay a small fee. You earn a share.',
        },
        skipped: {
          title: 'No service fee',
          description: 'Guests pay menu prices only.',
        },
      },
      footer: {
        enabled:
          'Guests pay {{percentage}}%, max {{max}} per order. Change it anytime in <settings>Order settings → Service Fee</settings>.',
        skipped: 'No service fee for now. Change it anytime in <settings>Order settings → Service Fee</settings>.',
      },
      enabled:
        'The service fee is now active on this restaurant at {{percentage}}%, capped at {{max}} per order. It applies to all new orders from now on. Change it anytime in <settings>Order settings → Service Fee</settings>.',
      enabled_partner_default:
        'The service fee is now active on this restaurant. Your partner default applies: {{percentage}}%, capped at {{max}} per order. It applies to all new orders from now on. Change it anytime in <settings>Order settings → Service Fee</settings>.',
      buttons: {
        enable: 'Enable service fee',
        continue_without: 'Continue without fee',
      },
      skipped:
        'Guests at this restaurant pay no service fee. You can turn it on anytime in <settings>Order settings → Service Fee</settings>.',
      toasts: {
        save_failed: 'Could not save the service fee choice. Please try again.',
      },
    },
  },
  toasts: {
    save_failed: 'Could not save your changes. Please try again.',
    saved: 'Saved',
  },
  optional: 'Optional',
  identity: {
    min_cuisine_hint: 'Select at least one cuisine to continue.',
    sections: {
      restaurant_type: {
        title: 'Restaurant type',
        description: 'What kind of establishment is this?',
      },
      cuisine: {
        title: 'Cuisine types',
        description: 'Pick at least one.',
      },
    },
  },
  menu: {
    modes: {
      recommended: 'Recommended',
      import: {
        title: 'Import from a platform',
        description: 'Pull the menu from GloriaFood, Wolt, Lieferando, Deliveroo and more.',
      },
      manual: {
        title: 'Build your menu from scratch',
        description: 'Create categories and items manually in the menu builder.',
      },
    },
    import: {
      title: 'Import from existing platform',
      in_progress: {
        title: 'Importing your menu…',
        description: 'This usually takes under two minutes. We will complete this step as soon as it finishes.',
      },
      failed: {
        title: 'The import did not finish',
        description: 'The menu import could not be completed. Please check the details and try again.',
      },
      blocked: {
        until: 'Paused until {{time}}',
        release: 'Release the link',
        released: 'Link released. You can start the import again.',
        release_failed: 'The link could not be released. Please try again in a moment.',
      },
      stalled: {
        title: 'Still importing…',
        description:
          'The import is taking longer than usual. It may still be running in the background — check again in a moment. Starting a new import now could import the menu twice.',
      },
    },
    manual: {
      title: 'Build your menu from scratch',
      description:
        'The menu builder opens with an empty menu. This step is complete once at least one category and one item exist.',
      open_editor: 'Open menu builder',
      waiting: {
        title: 'Waiting for your first menu item',
        description: 'Once you add an item in the menu editor, this step can be completed.',
      },
      found: {
        title: 'Menu items detected',
        description: 'Great — you can continue to the next step.',
      },
    },
    complete: {
      title: 'Menu ready',
      description: 'Your menu is in place. You can review it any time from the menu editor.',
    },
    buttons: {
      start_import: 'Start import',
      importing: 'Importing…',
      retry: 'Retry import',
      check_again: 'Check again',
      continue: 'Continue',
      skip: 'Skip for now',
    },
  },
  phases: {
    // Stands in for the restaurant name in phase descriptions before it is known.
    fallback_name: 'this restaurant',
    location: {
      title: 'Location',
      description: 'Where is the restaurant, and which brand does it belong to?',
    },
    identity: {
      title: 'Identity',
      description: 'Restaurant type and cuisines.',
    },
    brand: {
      title: 'Brand',
      description: 'Logo, colours and gallery.',
    },
    operations: {
      title: 'Location details',
      description:
        'Configure how {{name}} takes orders. This is the minimum required before any order can be received.',
    },
    menu: {
      title: 'Menu',
      description:
        "Get {{name}}'s menu onto the platform. Import it from an existing platform in under two minutes, or build it from scratch.",
    },
    domain: {
      title: 'Domain',
      description: 'The web address customers use to order from this restaurant.',
    },
    go_live: {
      title: 'Go Live',
      description: 'Enable live orders.',
    },
    payouts: {
      title: 'Online payment',
      description: 'How this restaurant takes payment online.',
    },
    service_fee: {
      title: 'Service fee',
      description: 'Whether guests pay a service fee at checkout. You can change it later in Order settings → Fees.',
    },
    bank_account: {
      title: 'Bank account',
      description: 'Where payouts go. Not needed to go live: payments wait in the balance until an account is added.',
    },
  },
  business_details: {
    locked: 'These details are verified with your payment provider. To change them, contact FoodAmigos.',
    section: {
      title: 'Business details',
    },
    fields: {
      business_type: {
        label: 'Business type',
        options: {
          company: 'Company (GmbH, UG, Ltd, Inc — a registered legal entity)',
          individual: 'Individual (you personally own and run the business)',
        },
      },
      owner_first_name: { label: 'Owner first name' },
      owner_last_name: { label: 'Owner last name' },
      legal_name: {
        label: 'Legal business name',
        individual: "You run the business personally, so the owner's name is used as the legal business name.",
      },
      legal_address: { label: 'Registered address' },
      legal_city: { label: 'City' },
      legal_state: { label: 'State or province' },
      legal_zip_code: { label: 'Postal code' },
      legal_country: { label: 'Country' },
      tax_id: {
        generic: 'Tax or registration number',
        personal: 'Only the last four digits are shown. Leave it as it is to keep the number unchanged.',
        hint: 'For example {{example}}.',
        invalid: 'This does not match the usual format for this country and business type, for example {{example}}.',
      },
      is_vat_registered: { label: 'This business is VAT registered' },
      vat_id: {
        label: 'VAT number',
        help: 'Starts with {{country}}, followed by 8 to 12 letters or digits.',
        invalid: 'The VAT number must start with {{country}}, followed by 8 to 12 letters or digits.',
        help_unprefixed: 'Your VAT or tax registration number, as issued.',
        invalid_unprefixed: 'This does not look like a VAT number: use 5 to 25 letters or digits.',
      },
    },
    toasts: {
      save_failed: 'Could not save your business details. Please try again.',
    },
  },
  brand: {
    fields: {
      short_name: {
        label: 'Short name',
        placeholder: 'e.g. Amigo Pizza',
        tooltip: 'A shorter display name used across the storefront and receipts.',
      },
      primary_color: {
        label: 'Primary colour',
        invalid: 'Enter a valid hex colour, e.g. #1A73E8.',
      },
    },
    sections: {
      short_name: 'Name',
      short_name_hint: 'Used across the storefront, receipts and notifications.',
      logo_and_colour: 'Logo & primary brand colour',
      logo: 'Upload logo',
      logo_hint: 'If you leave this empty, your brand logo is used.',
      gallery: 'Gallery photos',
      gallery_hint: 'Add up to {{max}} photos of your restaurant. Drag to reorder.',
      gallery_count: '{{count}} of {{max}} added',
    },
    toasts: {
      image_uploaded: 'Image uploaded',
      image_upload_failed: 'Could not upload the image. {{error}}',
      save_failed: 'Could not save your brand details. Please try again.',
    },
  },
  operations: {
    contact: {
      title: 'Contact',
      description: 'Used for order notifications and customer communications.',
      phone: {
        label: 'Restaurant phone number',
        placeholder: '+49 30 1234567',
        invalid: 'Enter a complete international phone number, e.g. +49 30 1234567',
      },
      email: { label: 'Restaurant email', placeholder: 'orders@restaurant.com' },
    },
    methods: {
      title: 'Order methods',
      description: 'How customers can order, and how long each method takes.',
      pickup: {
        label: 'Pickup',
        prep_time: { label: 'Average preparation time (min)' },
      },
      delivery: {
        label: 'Delivery',
        delivery_time: { label: 'Average delivery time (min)' },
        advanced_zones: 'Advanced zone setup',
      },
    },
    timing: {
      title: 'Order timing',
      description: 'When customers can place orders — now, later, or in advance.',
      at_least_one: 'Enable ASAP or scheduled ordering (at least one is required).',
      asap: { label: 'Accept ASAP orders' },
      scheduled: { label: 'Accept scheduled orders' },
      reminders: { label: 'Send an automated reminder before a scheduled order is due' },
      pre_order: {
        label: 'Allow pre-orders for future days',
        days: { label: 'How many days ahead (0-31)' },
      },
    },
    tax: {
      title: 'VAT',
      description:
        'The rate applied to this restaurant’s products. It starts from the rate for the region — change it only if this restaurant charges something else.',
      rate: { label: 'VAT rate', placeholder: 'e.g. 7' },
      dedicated: { label: 'VAT differs by product' },
    },
    hours: {
      title: 'Opening hours',
      description: 'The days and times this restaurant accepts orders.',
      same_every_day: 'Same hours every day',
      split_shifts: {
        title: 'This restaurant has split opening hours',
        description:
          'Some days have more than one opening period (for example a lunch and a dinner shift). The simple editor here shows one period per day, so we keep your existing hours as they are — edit split shifts in Order settings → Opening hours.',
      },
    },
    toasts: {
      save_failed: 'Could not save your operations settings. Please try again.',
    },
  },
  online_payment: {
    question: 'Do you want to take online payments for this restaurant?',
    yes: 'Yes',
    not_now: 'Not now',
    declined: 'Customers will pay on pickup or delivery. You can turn online payments on any time from Settings.',
    providers: {
      stripe: 'Stripe',
      adyen: 'Adyen',
    },
    waiting_for_partner: {
      staff: {
        title: 'Payments are not set up for your partner account yet',
        description:
          'An admin of your partner account needs to choose who processes payments before this restaurant can take online payments. You can carry on with the other steps in the meantime.',
      },
    },
    continue_to_go_live: 'Continue to Go Live',
    platform_line: 'Payments for this restaurant go through FoodAmigos payments.',
    only_connection: 'Payments go through {{name}} (your only connected provider).',
    partner_issue: {
      title: 'Your partner {{partner}} has to finish setting up their payment provider',
      description:
        'Their payment provider cannot onboard restaurants yet. We have let them know — your details are saved, so come back once they confirm it is done.',
      retry_hint: 'Once it is fixed, continue to the provider again and we will retry.',
      open_settings: 'Open your payment gateway settings',
    },
    connect_another: 'Connect another provider',
    checking: 'Checking with {{provider}}…',
    checking_generic: 'Checking with the payment provider…',
    retry: 'Try again',
    not_set_up: {
      title: 'No payment provider linked yet',
      description:
        'Your partner links a payment provider to this restaurant. Once they have, you can finish setup here.',
    },
    choice: {
      title: 'Which of your connected providers should this restaurant use?',
      primary: 'Primary',
      confirm: 'Use this provider',
      cancel: 'Cancel',
    },
    using: 'Using {{name}}',
    change: 'Change',
    payout_details: {
      title: 'Business details for payouts',
      description: 'Required by the payment provider to verify your business and send payouts.',
      business_type: {
        company: {
          title: 'Company',
          description: 'GmbH, UG, Ltd, Inc — a registered legal entity',
        },
        individual: {
          title: 'Individual',
          description: 'You personally own and run the business',
        },
      },
      registered_address: 'Registered address',
      legal_address: 'Street address',
      legal_state: 'State / region',
      vat_id: 'VAT ID',
      optional: '(optional)',
      fill_first: 'Fill in the business details for payouts above first.',
    },
    hand_off: {
      title: 'Next: finish setup with {{provider}}',
      description:
        "{{provider}} will ask for the owner's identity document on its own form, using the business details for payouts above. About 10 minutes. The bank account comes after Go Live.",
      cta: 'Continue to {{provider}}',
      transition: 'Taking you to {{provider}}…',
      error: 'We could not open the {{provider}} form. Please try again.',
    },
    states: {
      verified: 'Payments are live.',
      submitted: {
        title: 'Details sent to {{provider}}',
        description: "Verification usually takes 1–2 business days. We'll email the restaurant owner when it's done.",
        description_email: "Verification usually takes 1–2 business days. We'll email {{email}} when it's done.",
        review: {
          business_type: 'Business type',
          legal_name: 'Legal name',
          address: 'Registered address',
          types: {
            company: 'Company',
            individual: 'Individual',
          },
        },
      },
      incomplete: {
        title: "You haven't finished with {{provider}}",
        description: 'Your details are saved — the link is valid for 2 days.',
        expired: 'Your details are saved, but the link has expired.',
        cta: 'Continue with {{provider}}',
        new_link: 'Get a new link and continue',
        check_details: 'Check my details first',
      },
      action_needed: {
        title: '{{provider}} needs one more thing',
        description: "{{provider}} couldn't verify one of the details you gave. Open their form again to fix it.",
        cta: 'Open the {{provider}} form again',
      },
    },
    connected: {
      title: 'Payments go through {{name}}',
      description: 'This restaurant is linked to the {{name}} account — nothing else to set up here.',
      failed: 'We could not link this restaurant to {{name}} yet',
    },
    errors: {
      generic: 'Something went wrong. Please try again.',
      load_failed: "We couldn't load this restaurant's payment setup.",
    },
  },
  bank_account: {
    section: {
      title: 'Payout bank account',
      description: 'Payouts are sent in {{currency}}, the currency this restaurant takes payments in.',
    },
    done: 'Back to dashboard',
    load_failed: "We couldn't load this restaurant's bank account.",
    retry: 'Try again',
    no_permission: 'Only the restaurant owner or the partner can enter the bank account.',
    confirm_with_provider: 'Confirm with Adyen',
    toasts: {
      save_failed: "We couldn't save the bank account. Please check the details.",
    },
  },
  payouts: {
    finish_setup: 'Finish setup',
  },
};
